
<template>
    <report
        :report="report"
        :versions="versions"
        :loading="loading"
        :refreshing="refreshing"
        @reload="reload"
        @load-version="load_version"
    >
        <div class="lam-graph-container">
            <div class="uk-grid uk-grid-small uk-grid-width-1-2">
                <div>
                    <ul class="uk-subnav uk-subnav-line">
                        <li class="uk-disabled"><a href="">Capacity</a></li>
                        <li
                            v-for="capacity in capacities"
                            :key="capacity"
                            :class="{'uk-active': state.filter === capacity}"
                        >
                            <a href @click.prevent="set_state_value('filter', capacity)">
                                {{ capacity }}
                            </a>
                        </li>
                    </ul>
                </div>
                <div class="uk-text-right">
                    <div class="uk-button-group lam-graph-filter">
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.mode === 'absolute'}"
                            @click.prevent="set_state_value('mode', 'absolute')"
                        >
                            Absolute
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.mode === 'relative'}"
                            @click.prevent="set_state_value('mode', 'relative')"
                        >
                            Relative
                        </base-button>
                    </div>

                    <div class="uk-button-group lam-graph-filter uk-margin-small-left">
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.units === 'count'}"
                            @click.prevent="set_state_value('units', 'count')"
                        >
                            By Count
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.units === 'total'}"
                            @click.prevent="set_state_value('units', 'total')"
                        >
                            By Fee
                        </base-button>
                    </div>

                    <div class="uk-button-group lam-graph-filter uk-margin-small-left">
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 1}"
                            @click.prevent="set_state_value('scope', 1)"
                        >
                            One Year
                        </base-button>
                        <base-button
                            size="mini"
                            :class="{'uk-active': state.scope === 2}"
                            @click.prevent="set_state_value('scope', 2)"
                        >
                            Two Years
                        </base-button>
                    </div>
                </div>
            </div>
            <div class="uk-grid uk-grid-small uk-margin-bottom lam-graph">
                <div class="uk-width-1-1" style="position: relative;">
                    <projects-by-types-graph
                        ref="chart"
                        :chart-data="chart_data"
                        :display-mode="state.mode"
                        :units="state.units"
                        :y-max="ymax"
                        :legend-position="!!state.category_id ? 'bottom' : 'right'"
                        style="height: 600px;"
                    />
                </div>
            </div>
        </div>
    </report>
</template>

<script type="text/javascript">
import is_report from "./IsReport"
import handles_projects from "./HandlesProjects"
import uses_color_palette from "../../mixins/UsesColorPalette"

import Report from "./Report.vue"
import BaseButton from "../../ui/BaseButton"
import ProjectsByTypesGraph from "./ProjectsByTypesGraph.js"

export default {
    mixins: [is_report, handles_projects, uses_color_palette],
    components: {
        Report,
        BaseButton,
        ProjectsByTypesGraph
    },
    watch: {
        "state.category_id": "load",
        "state.scope": "load"
    },
    methods: {
        zoom (zoom) {
            this.set_state_value("category_id", zoom ? this.report.dataset[0].id : 0)
        }
        /*
        zoom (category_name) {
            const category = this.report.dataset.find(category => category.name === category_name)
            this.set_state_value("category_id", category ? category.id : 0)
        }
        */
    },
    computed: {
        capacities () {
            return [
                "Any",
                this.$root.constants.terms.PROJECT_USER_ROLE_PIC,
                this.$root.constants.terms.PROJECT_USER_ROLE_PM,
                this.$root.constants.terms.PROJECT_USER_ROLE_DESIGNER
            ]
        },
        chart_data () {
            const data = { labels: [], datasets: [] }

            if(this.report && this.report.dataset && !!this.report.dataset.length) {
                Object.keys(this.report.dataset[0].data).forEach(initials => {
                    if(this.state.filter === "Any") data.labels.push(initials)
                    else {
                        const found = Object.values(this.report.dataset).find(category => {
                            return !!category.data[initials][this.state.filter].count || !!category.data[initials][this.state.filter].total
                        })
                        if(found) data.labels.push(initials)
                    }
                })
                const grand_totals = {}
                if(this.state.mode === "relative") {
                    data.labels.forEach(initials => {
                        grand_totals[initials] = 0
                        this.report.dataset.forEach(category => {
                            grand_totals[initials] += category.data[initials][this.state.filter][this.state.units]
                        })
                    })
                }
                this.report.dataset.forEach(category => {
                    const color = this.assigned_color(category.name, category.color ? { background: category.color, border: category.color } : null)

                    const dataset_data = []
                    data.labels.forEach(initials => {
                        let value = category.data[initials][this.state.filter][this.state.units]
                        if(this.state.mode === "relative") {
                            value = grand_totals[initials] ? (100 * (value / grand_totals[initials])) : 0
                        }
                        dataset_data.push(value)
                    })

                    data.datasets.push({
                        label: this.nibnut_filter("nibnut.smart-truncate", [category.name, 30]),
                        backgroundColor: color.background,
                        borderColor: color.border,
                        borderWidth: 1,
                        categoryPercentage: 0.9,
                        barPercentage: 0.8,
                        data: dataset_data
                    })
                })

                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.ymax = 0
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                if(this.state.mode === "relative") this.ymax = 100
                else {
                    for(let loop = 0; loop < data.labels.length; loop++) {
                        let category_total = 0
                        data.datasets.forEach(dataset => {
                            category_total += parseInt(dataset.data[loop]) || 0
                        })
                        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                        this.ymax = Math.max(category_total, this.ymax)
                    }
                }
            }

            return data
        }
    },
    data () {
        return {
            report_id: "projects-by-types",

            default_state: {
                scope: 1,
                mode: "absolute",
                filter: "Any", // capacity
                units: "count",
                category_id: 0,
                version_id: 0
            }
        }
    }
}
</script>
